import axios from 'axios';
import { RUNTIME_CONFIG } from '../config/runtime';

const API_BASE_URL = RUNTIME_CONFIG.API_URL;

interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
  preferredName?: string;
  emailVerified: boolean;
  attributes?: Record<string, any>;
}

interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  user?: UserInfo;
  tokens?: AuthTokens;
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private idToken: string | null = null;
  private tokenExpiresAt: number | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  // Refresh deduplication to prevent rate limiting
  private refreshPromise: Promise<void> | null = null;
  private lastRefreshTime: number = 0;
  private readonly REFRESH_COOLDOWN = 10000; // 10 seconds minimum between refresh attempts

  constructor() {
    this.loadTokensFromStorage();
    this.setupAxiosInterceptors();
    // Temporarily disabled to prevent Cognito rate limiting (tokens are valid for 8 hours)
    // this.scheduleTokenRefresh();
  }

  private loadTokensFromStorage(): void {
    console.log('🔓 Loading tokens from localStorage...');
    const storedTokens = localStorage.getItem('authTokens');
    console.log('🔓 Stored tokens found:', !!storedTokens);

    if (storedTokens) {
      try {
        const tokens = JSON.parse(storedTokens);
        console.log('🔓 Parsed tokens:', {
          hasAccessToken: !!tokens.accessToken,
          hasRefreshToken: !!tokens.refreshToken,
          hasIdToken: !!tokens.idToken,
          expiresAt: tokens.expiresAt ? new Date(tokens.expiresAt).toISOString() : 'N/A',
          storedAt: tokens.storedAt ? new Date(tokens.storedAt).toISOString() : 'N/A'
        });

        this.accessToken = tokens.accessToken;
        this.refreshToken = tokens.refreshToken;
        this.idToken = tokens.idToken;
        this.tokenExpiresAt = tokens.expiresAt;

        console.log('🔓 Tokens loaded into memory | expiresAt:', this.tokenExpiresAt ? new Date(this.tokenExpiresAt).toISOString() : 'N/A');

        // DISABLED: Aggressive token upgrade check was causing login loops
        // If we want to upgrade tokens, do it passively during token refresh
        // instead of forcing logout immediately after login

        /*
        // Only upgrade tokens if they're OLD (stored more than 24 hours ago)
        // AND have less than 1 hour remaining
        if (this.tokenExpiresAt && this.accessToken) {
          const now = Date.now();
          const timeRemaining = this.tokenExpiresAt - now;
          const oneHour = 60 * 60 * 1000;
          const twentyFourHours = 24 * 60 * 60 * 1000;

          const storedAt = tokens.storedAt || now;
          const timeSinceStored = now - storedAt;

          // Only force re-auth if token is both OLD and EXPIRING SOON
          if (timeSinceStored > twentyFourHours && timeRemaining < oneHour && timeRemaining > 0) {
            console.log('Detected old, soon-to-expire token, forcing re-authentication');
            this.clearTokens();
            if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/auth/')) {
              window.location.href = '/login?reason=token_upgrade';
            }
          }
        }
        */
      } catch (error) {
        console.error('Failed to parse stored tokens:', error);
        this.clearTokens();
      }
    }
  }

  private saveTokensToStorage(): void {
    if (this.accessToken && this.refreshToken) {
      const tokens = {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
        idToken: this.idToken,
        expiresAt: this.tokenExpiresAt,
        storedAt: Date.now(), // Track when tokens were stored
      };
      localStorage.setItem('authTokens', JSON.stringify(tokens));
    }
  }

  private clearTokens(): void {
    console.log('🗑️ CLEARING TOKENS - Stack trace:');
    console.trace();

    this.accessToken = null;
    this.refreshToken = null;
    this.idToken = null;
    this.tokenExpiresAt = null;
    localStorage.removeItem('authTokens');
    localStorage.removeItem('user');

    console.log('🗑️ Tokens cleared from memory and localStorage');

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private setupAxiosInterceptors(): void {
    // Request interceptor to add auth token
    axios.interceptors.request.use(
      (config) => {
        if (this.accessToken && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle 401 errors
    // This interceptor is disabled - token refresh is handled by api.ts interceptor instead
    // to avoid duplicate refresh attempts and rate limiting issues
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (this.tokenExpiresAt && this.refreshToken) {
      const now = Date.now();
      const expiresIn = this.tokenExpiresAt - now;
      
      // Refresh 15 minutes before expiration to provide more buffer
      const refreshIn = Math.max(0, expiresIn - 15 * 60 * 1000);
      
      if (refreshIn > 0) {
        this.refreshTimer = setTimeout(() => {
          this.refreshAccessToken();
        }, refreshIn);
      }
    }
  }

  async register(
    email: string,
    password: string,
    name: string,
    phone?: string,
    preferredName?: string,
    stylePreferences?: string
  ): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        email,
        password,
        name,
        phone,
        preferred_name: preferredName,
        style_preferences: stylePreferences,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  }

  async confirmRegistration(email: string, code: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/confirm`, {
        email,
        code,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Confirmation failed',
      };
    }
  }

  async resendConfirmationCode(email: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-code`, {
        email,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend code',
      };
    }
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      console.log('🔐 Login attempt for:', email);
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      console.log('🔐 Login response:', {
        success: response.data.success,
        hasAccessToken: !!response.data.access_token,
        hasRefreshToken: !!response.data.refresh_token,
        hasIdToken: !!response.data.id_token,
        expiresIn: response.data.expires_in,
        hasUser: !!response.data.user
      });

      if (response.data.success) {
        this.accessToken = response.data.access_token;
        this.refreshToken = response.data.refresh_token;
        this.idToken = response.data.id_token;
        this.tokenExpiresAt = Date.now() + response.data.expires_in * 1000;

        console.log('🔐 Tokens stored:', {
          tokenExpiresAt: new Date(this.tokenExpiresAt).toISOString(),
          timeUntilExpiry: Math.floor((this.tokenExpiresAt - Date.now()) / 1000) + 's'
        });

        this.saveTokensToStorage();
        this.scheduleTokenRefresh();

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          console.log('🔐 User stored:', response.data.user.email);
        }
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Login failed:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  }

  async logout(): Promise<void> {
    console.log('🚪 LOGOUT called - Stack trace:');
    console.trace();
    try {
      if (this.accessToken) {
        await axios.post(`${API_BASE_URL}/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to initiate password reset',
      };
    }
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed',
      };
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password change failed',
      };
    }
  }

  async refreshAccessToken(): Promise<void> {
    // If a refresh is already in progress, wait for it instead of starting a new one
    if (this.refreshPromise) {
      console.log('🔄 Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    // Check cooldown to prevent rapid successive refresh attempts
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastRefreshTime;
    if (timeSinceLastRefresh < this.REFRESH_COOLDOWN) {
      const remaining = Math.ceil((this.REFRESH_COOLDOWN - timeSinceLastRefresh) / 1000);
      console.log(`🔄 Refresh cooldown active, ${remaining}s remaining. Skipping refresh.`);
      return; // Skip refresh if we're in cooldown period
    }

    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Create a shared promise that all concurrent refresh attempts will await
    this.refreshPromise = (async () => {
      try {
        console.log('🔄 Starting token refresh...');
        this.lastRefreshTime = Date.now();

        // NOTE: We don't send username because we only have email, not Cognito username (sub)
        // Cognito's REFRESH_TOKEN_AUTH can identify the user from the refresh token itself
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: this.refreshToken,
          // username intentionally omitted - Cognito doesn't need it for REFRESH_TOKEN_AUTH
        });

        if (response.data.success) {
          this.accessToken = response.data.access_token;
          this.idToken = response.data.id_token;
          this.tokenExpiresAt = Date.now() + response.data.expires_in * 1000;

          this.saveTokensToStorage();
          console.log('✅ Token refresh successful - new expiry:', new Date(this.tokenExpiresAt).toISOString());
          // DO NOT call scheduleTokenRefresh() - it's disabled to prevent rate limiting
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (error: any) {
        console.error('❌ Token refresh failed:', error.response?.status, error.message);

        // Handle rate limiting specially - don't clear tokens immediately
        if (error.response?.status === 429) {
          console.log('⚠️ Rate limited by Cognito. Tokens still valid for now.');
          // Don't clear tokens - they're still valid, just can't refresh yet
          throw new Error('RATE_LIMITED');
        }

        this.clearTokens();
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async getProfile(): Promise<UserInfo | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/profile`);
      
      if (response.data.success && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get profile:', error);
      return null;
    }
  }

  async inviteUser(email: string, message?: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/invite`, {
        email,
        message,
      });
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send invitation',
      };
    }
  }

  async deleteAccount(): Promise<AuthResponse> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/auth/delete-account`);
      
      if (response.data.success) {
        this.clearTokens();
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete account',
      };
    }
  }

  isAuthenticated(): boolean {
    // Increased buffer from 60s to 5 minutes to be much more lenient
    // Tokens are valid for 8 hours, so 5-minute buffer is safe
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    const hasToken = !!this.accessToken;
    const hasExpiry = !!this.tokenExpiresAt;
    const notExpired = hasExpiry && (this.tokenExpiresAt ?? 0) > (now + bufferTime);

    const result = hasToken && hasExpiry && notExpired;

    // Debug logging
    if (!result) {
      const timeUntilExpiry = hasExpiry ? Math.floor(((this.tokenExpiresAt ?? 0) - now) / 1000) : 0;
      console.log('🔒 isAuthenticated FAILED | hasToken:', hasToken, '| hasExpiry:', hasExpiry, '| notExpired:', notExpired, '| timeLeft:', timeUntilExpiry + 's');

      // Also check localStorage
      const stored = localStorage.getItem('authTokens');
      console.log('🔒 localStorage authTokens exists:', !!stored);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          console.log('🔒 Stored token expiry:', new Date(parsed.expiresAt).toISOString(), '| Now:', new Date(now).toISOString());
        } catch (e) {
          console.log('🔒 Failed to parse stored tokens');
        }
      }
    }

    return result;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getIdToken(): string | null {
    return this.idToken;
  }

  getCurrentUser(): UserInfo | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Failed to parse user data:', error);
        return null;
      }
    }
    return null;
  }

  forceLogoutForTokenUpgrade(): void {
    console.log('Forcing logout to upgrade to longer-lived tokens');
    this.clearTokens();
    if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/auth/')) {
      window.location.href = '/login?reason=token_upgrade&message=Please%20log%20in%20again%20for%20extended%20session';
    }
  }

  updateCurrentUser(updates: Partial<UserInfo>): void {
    const current = this.getCurrentUser();
    if (current) {
      const updated = { ...current, ...updates };
      localStorage.setItem('user', JSON.stringify(updated));
    }
  }
}

// Create singleton instance
const authService = new AuthService();
export default authService;